import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Sliders,
  Plus,
  Trash,
  Play,
  Pencil,
  Lightning,
  User,
  Funnel,
  ArrowDown,
  ArrowUp
} from '@phosphor-icons/react';
import { FilterBuilder } from '../components/FilterBuilder';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AllocationRulesPage = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [structures, setStructures] = useState([]);
  const [branchUsers, setBranchUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [running, setRunning] = useState(false);

  // Form state
  const [formUserId, setFormUserId] = useState('');
  const [formStructureId, setFormStructureId] = useState('');
  const [formConditions, setFormConditions] = useState([]);
  const [formLogic, setFormLogic] = useState('and');
  const [formPriority, setFormPriority] = useState(0);

  const fetchRules = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/allocation-rules`);
      setRules(data);
    } catch (e) {
      toast.error('Failed to fetch rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    axios.get(`${API_URL}/api/data-structures`).then(res => setStructures(res.data)).catch(() => {});
    axios.get(`${API_URL}/api/users`).then(res => setBranchUsers(res.data)).catch(() => {});
  }, [fetchRules]);

  const getFilterFields = () => {
    const allFields = new Set(['status']);
    if (formStructureId) {
      const s = structures.find(st => st.id === formStructureId);
      if (s) (s.fields || []).forEach(f => allFields.add(f.name));
    } else {
      // When no schema selected, show union of all fields
      structures.forEach(s => {
        (s.fields || []).forEach(f => allFields.add(f.name));
      });
    }
    return Array.from(allFields);
  };

  const openCreateDialog = () => {
    setEditingRule(null);
    setFormUserId('');
    setFormStructureId('');
    setFormConditions([{ field: '', operator: 'equals', value: '' }]);
    setFormLogic('and');
    setFormPriority(rules.length);
    setIsDialogOpen(true);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormUserId(rule.user_id);
    setFormStructureId(rule.structure_id || '');
    setFormConditions(rule.conditions || []);
    setFormLogic(rule.logic || 'and');
    setFormPriority(rule.priority || 0);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formUserId) {
      toast.error('Please select a user');
      return;
    }
    const validConditions = formConditions.filter(c => c.field && c.value);
    if (validConditions.length === 0) {
      toast.error('Please add at least one condition');
      return;
    }

    try {
      if (editingRule) {
        await axios.put(`${API_URL}/api/allocation-rules/${editingRule.id}`, {
          user_id: formUserId,
          structure_id: formStructureId || null,
          conditions: validConditions,
          logic: formLogic,
          priority: formPriority
        });
        toast.success('Rule updated');
      } else {
        await axios.post(`${API_URL}/api/allocation-rules`, {
          user_id: formUserId,
          structure_id: formStructureId || null,
          conditions: validConditions,
          logic: formLogic,
          priority: formPriority
        });
        toast.success('Rule created');
      }
      setIsDialogOpen(false);
      fetchRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this allocation rule?')) return;
    try {
      await axios.delete(`${API_URL}/api/allocation-rules/${ruleId}`);
      toast.success('Rule deleted');
      fetchRules();
    } catch (e) {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggle = async (rule) => {
    try {
      await axios.put(`${API_URL}/api/allocation-rules/${rule.id}`, {
        is_active: !rule.is_active
      });
      fetchRules();
    } catch (e) {
      toast.error('Failed to update rule');
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/allocation-rules/run`, {});
      toast.success(data.message);
    } catch (e) {
      toast.error('Failed to run allocation');
    } finally {
      setRunning(false);
    }
  };

  const getOperatorLabel = (op) => {
    const labels = { contains: 'contains', equals: '=', not_equals: '!=', starts_with: 'starts with', gt: '>', lt: '<', gte: '>=', lte: '<=' };
    return labels[op] || op;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton"></div>
        <div className="h-64 skeleton rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="allocation-rules-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Lightning size={28} weight="duotone" className="text-primary" />
            Auto-Allocation Rules
          </h1>
          <p className="text-muted-foreground">Automatically assign new records to team members based on criteria</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRunNow} disabled={running} data-testid="run-allocation-btn">
            <Play size={18} className="mr-2" />
            {running ? 'Running...' : 'Run Now'}
          </Button>
          <Button onClick={openCreateDialog} data-testid="add-rule-btn">
            <Plus size={18} className="mr-2" /> Add Rule
          </Button>
        </div>
      </div>

      <Card className="p-3 bg-secondary/30">
        <p className="text-sm text-muted-foreground">
          Rules run automatically when new records are uploaded or created. They are processed top-to-bottom by priority — the first matching rule assigns the record. Use "Run Now" to apply rules to existing unassigned records.
        </p>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Priority</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Schema</TableHead>
                <TableHead>Conditions</TableHead>
                <TableHead>Logic</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.length > 0 ? rules.map((rule) => (
                <TableRow key={rule.id} className={!rule.is_active ? 'opacity-50' : ''} data-testid={`rule-row-${rule.id}`}>
                  <TableCell>
                    <span className="font-mono text-sm font-bold">{rule.priority}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-muted-foreground" />
                      <span className="font-medium">{rule.user_name || '-'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{structures.find(s => s.id === rule.structure_id)?.name || 'All'}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(rule.conditions || []).map((c, i) => (
                        <Badge key={i} variant="outline" className="text-xs font-mono">
                          {c.field} {getOperatorLabel(c.operator)} "{c.value}"
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">{(rule.logic || 'and').toUpperCase()}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(rule)}
                      className={rule.is_active ? 'text-green-600' : 'text-muted-foreground'}
                    >
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(rule)} className="h-8 w-8">
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(rule.id)} className="h-8 w-8 text-destructive">
                        <Trash size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    <Sliders size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No allocation rules yet</p>
                    <p className="text-xs mt-1">Create a rule to automatically assign records to team members</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Rule Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit' : 'Create'} Allocation Rule</DialogTitle>
            <DialogDescription>
              Define conditions that match incoming records and assign them to a specific user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Assign To</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" value={formUserId} onChange={(e) => setFormUserId(e.target.value)} data-testid="rule-user-select">
                  <option value="">Select a user</option>
                  {branchUsers.filter(u => u.role !== 'super_admin').map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role.replace('_', ' ')})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Schema</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" value={formStructureId} onChange={(e) => { setFormStructureId(e.target.value); setFormConditions([{ field: '', operator: 'equals', value: '' }]); }} data-testid="rule-schema-select">
                  <option value="">All Schemas</option>
                  {structures.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Priority (lower = higher priority)</Label>
                <Input
                  type="number"
                  value={formPriority}
                  onChange={(e) => setFormPriority(parseInt(e.target.value) || 0)}
                  min={0}
                  data-testid="rule-priority-input"
                />
              </div>
            </div>

            <div className="border rounded-md p-4">
              <FilterBuilder
                conditions={formConditions}
                setConditions={setFormConditions}
                logic={formLogic}
                setLogic={setFormLogic}
                fields={getFilterFields()}
                onApply={() => {}}
                onClear={() => setFormConditions([{ field: '', operator: 'equals', value: '' }])}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="save-rule-btn">
                {editingRule ? 'Update' : 'Create'} Rule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllocationRulesPage;
